// Shared "Sandbox Test" flow for external agents — a full round-trip check:
// dispatches a real sample invocation to the developer's webhook, then waits
// for their server to call back with the actual answer via
// POST /api/v1/agent-response (the initial webhook ack alone only proves
// they received the question, not that they can answer it).
//
// Used by the Sandbox tab in AgentFormModal (the one modal that now handles
// every agent status — registration, drafts, and ongoing management alike),
// so a developer can verify their integration works before ever submitting
// for review.
import { useState, useEffect } from 'react'
import { useSandboxDispatchMutation, usePollInvocationQuery } from '../../store/api/developerApi'
import { CodeBlock, CopyButton } from '../../components/ui'
import { SANDBOX_RESPONSE_TIMEOUT_S, SANDBOX_INVOCATION_POLL_MS, SANDBOX_TIMEOUT_WARNING_THRESHOLD_S } from '../../lib/config'
import s from '../../styles/pages/DeveloperPage.module.css'

function prettyJson(str: string) { try { return JSON.stringify(JSON.parse(str), null, 2) } catch { return str } }

// ── Sandbox response waiter ──────────────────────────────────
function SandboxResponseWaiter({
  agentId, invocationId, apiKeyPrefix,
}: {
  agentId: string
  invocationId: string
  apiKeyPrefix: string | null
}) {
  const TIMEOUT_S = SANDBOX_RESPONSE_TIMEOUT_S
  const [elapsed, setElapsed] = useState(0)
  const [timedOut, setTimedOut] = useState(false)
  const [responded, setResponded] = useState(false)

  const { data } = usePollInvocationQuery(
    { agentId, invocationId },
    { pollingInterval: timedOut || responded ? 0 : SANDBOX_INVOCATION_POLL_MS }
  )
  const inv = data?.invocation

  useEffect(() => {
    if (inv?.status === 'responded') setResponded(true)
  }, [inv?.status])

  useEffect(() => {
    if (timedOut || responded) return
    const interval = setInterval(() => {
      setElapsed(e => {
        const next = e + 1
        if (next >= TIMEOUT_S) { setTimedOut(true); clearInterval(interval) }
        return next
      })
    }, 1000)
    return () => clearInterval(interval)
  }, [timedOut, responded])

  // Build curl snippet
  const curlSnippet = `curl -X POST http://localhost:3000/api/v1/agent-response \\\n  -H 'Content-Type: application/json' \\\n  -H 'Authorization: Bearer ${apiKeyPrefix ? apiKeyPrefix + '...' : 'YOUR_API_KEY'}' \\\n  -d '${JSON.stringify({
    invocationId,
    content: 'Your agent response goes here.',
  }, null, 2)}'`

  // ── Responded — show validation ─────────────────────────────
  if (inv?.status === 'responded' && inv.validation) {
    const { checks, passed } = inv.validation
    const checkItems = [
      { key: 'invocationMatched', label: 'invocationId matched',     ok: checks.invocationMatched },
      { key: 'agentMatched',      label: 'agentId matched',          ok: checks.agentMatched },
      { key: 'contentPresent',    label: 'content field present',    ok: checks.contentPresent },
      { key: 'contentNonEmpty',   label: 'content is non-empty',     ok: checks.contentNonEmpty },
      { key: 'withinTimeWindow',  label: `responded within ${SANDBOX_RESPONSE_TIMEOUT_S}s (${inv.responseMs}ms)`, ok: checks.withinTimeWindow },
    ]
    return (
      <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '0.85rem', fontWeight: 700, color: passed ? 'var(--success)' : 'var(--error)' }}>
            {passed ? '✅ Response validated — format is correct' : '⚠️ Response received with issues'}
          </span>
          <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{inv.responseMs}ms</span>
        </div>
        <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
          {checkItems.map(item => (
            <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.45rem 0.75rem', borderBottom: '1px solid var(--border)', fontSize: '0.78rem' }}>
              <span style={{ color: item.ok ? 'var(--success)' : 'var(--error)', fontSize: '0.85rem', flexShrink: 0 }}>{item.ok ? '✓' : '✗'}</span>
              <span style={{ color: item.ok ? 'var(--text-primary)' : 'var(--error)' }}>{item.label}</span>
            </div>
          ))}
        </div>
        {inv.responseBody && (
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>
            <div style={{ fontWeight: 700, marginBottom: '0.2rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Content received</div>
            <div style={{ position: 'relative' }}>
              <CodeBlock maxHeight={120}>{inv.responseBody}</CodeBlock>
              <div style={{ position: 'absolute', top: '0.35rem', right: '0.35rem' }}>
                <CopyButton text={inv.responseBody} small />
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  // ── Timed out ─────────────────────────────────────────────
  if (timedOut || inv?.status === 'timed_out') {
    return (
      <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        <div style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--error)' }}>
          ⏱ {SANDBOX_RESPONSE_TIMEOUT_S}s elapsed — no response received
        </div>
        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
          Your webhook was acknowledged, but your server never called back to <code>POST /api/v1/agent-response</code>.
          Make sure your handler sends the response back after processing. Use the curl below to test it manually:
        </div>
        <div style={{ position: 'relative' }}>
          <CopyButton text={curlSnippet} small />
          <CodeBlock maxHeight={160} fontSize="0.68rem">{curlSnippet}</CodeBlock>
        </div>
      </div>
    )
  }

  // ── Waiting ───────────────────────────────────────────────
  const remaining = TIMEOUT_S - elapsed
  return (
    <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
      <style>{`@keyframes sandbox-test-spin { to { transform: rotate(360deg) } }`}</style>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
        <div style={{ width: 14, height: 14, border: '2px solid var(--border)', borderTopColor: 'var(--warning)', borderRadius: '50%', animation: 'sandbox-test-spin 0.8s linear infinite', flexShrink: 0 }} />
        <span style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-primary)' }}>
          Waiting for your server to call back… <span style={{ color: remaining <= SANDBOX_TIMEOUT_WARNING_THRESHOLD_S ? 'var(--error)' : 'var(--text-muted)', fontWeight: 700 }}>{remaining}s</span>
        </span>
      </div>
      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
        Your server received the webhook and returned HTTP 200. Now it needs to POST the response back to Ekkam.
        You can trigger it manually with this curl:
      </div>
      <div style={{ position: 'relative' }}>
        <CopyButton text={curlSnippet} small />
        <CodeBlock maxHeight={160} fontSize="0.68rem">{curlSnippet}</CodeBlock>
      </div>
      <div style={{ height: 4, borderRadius: 2, background: 'var(--border)', overflow: 'hidden' }}>
        <div style={{ height: '100%', borderRadius: 2, background: remaining <= SANDBOX_TIMEOUT_WARNING_THRESHOLD_S ? 'var(--error)' : 'var(--warning)', width: `${(elapsed / TIMEOUT_S) * 100}%`, transition: 'width 1s linear, background 0.3s' }} />
      </div>
    </div>
  )
}

// ── Sandbox dispatch panel ────────────────────────────────────
export function SandboxDispatchPanel({ agentId, apiKeyPrefix }: { agentId: string; apiKeyPrefix: string | null }) {
  const [dispatch, { isLoading }] = useSandboxDispatchMutation()
  const [result, setResult] = useState<{
    invocationId: string; payloadSent: string; success: boolean
    statusCode: number; responseMs: number; responseBody?: string; error?: string
  } | null>(null)
  const [showPayload, setShowPayload] = useState(false)
  const [showResponse, setShowResponse] = useState(false)
  const [showWaiter, setShowWaiter] = useState(false)

  const run = async () => {
    setResult(null); setShowPayload(false); setShowResponse(false); setShowWaiter(false)
    try {
      const res = await dispatch(agentId).unwrap()
      setResult(res)
      if (res.success) { setShowPayload(true); setShowResponse(true); setShowWaiter(true) }
    } catch (err: any) {
      setResult({ invocationId: '', payloadSent: '', success: false, statusCode: 0, responseMs: 0, error: err?.data?.message ?? 'Dispatch failed' })
    }
  }

  return (
    <div className={s.testPanel}>
      <div className={s.testHeader}>
        <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Full round-trip — real invocation, real webhook, captures payload &amp; response</span>
        <button onClick={() => void run()} disabled={isLoading} className={s.primaryBtn} style={{ flexShrink: 0 }}>
          {isLoading ? '⏳ Running...' : '▶ Run Sandbox'}
        </button>
      </div>
      {result && (
        <div className={s.testResult} style={{ borderColor: result.success ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)', background: result.success ? 'rgba(34,197,94,0.05)' : 'rgba(239,68,68,0.05)' }}>
          <div className={s.testResultHeader}>
            <span style={{ fontWeight: 700, color: result.success ? 'var(--success)' : 'var(--error)' }}>
              {result.success ? '✅ Webhook acknowledged' : '❌ Webhook failed'}
            </span>
            {result.statusCode > 0 && <code style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>HTTP {result.statusCode}</code>}
            <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginLeft: 'auto' }}>{result.responseMs}ms</span>
          </div>
          {result.error && <div style={{ fontSize: '0.78rem', color: 'var(--error)', marginTop: '0.35rem' }}>{result.error}</div>}
          {result.invocationId && (
            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '0.35rem', display: 'flex', alignItems: 'center', gap: '0.35rem', flexWrap: 'wrap' }}>
              <span>Invocation ID:</span>
              <code style={{ fontSize: '0.7rem' }}>{result.invocationId}</code>
              <CopyButton text={result.invocationId} small />
            </div>
          )}
          {result.payloadSent && (
            <div style={{ marginTop: '0.5rem' }}>
              <button onClick={() => setShowPayload(v => !v)} className={s.ghostBtnSm} style={{ fontSize: '0.72rem' }}>
                {showPayload ? '▲ Hide payload sent' : '▼ Show payload sent'}
              </button>
              {showPayload && (
                <div style={{ position: 'relative', marginTop: '0.35rem' }}>
                  <CodeBlock style={{ marginTop: '0.2rem' }}>{prettyJson(result.payloadSent)}</CodeBlock>
                  <div style={{ position: 'absolute', top: '0.35rem', right: '0.35rem' }}>
                    <CopyButton text={prettyJson(result.payloadSent)} small />
                  </div>
                </div>
              )}
            </div>
          )}
          {result.responseBody !== undefined && (
            <div style={{ marginTop: '0.35rem' }}>
              <button onClick={() => setShowResponse(v => !v)} className={s.ghostBtnSm} style={{ fontSize: '0.72rem' }}>
                {showResponse ? '▲ Hide response body' : '▼ Show response body'}
              </button>
              {showResponse && (
                <div style={{ position: 'relative', marginTop: '0.35rem' }}>
                  <CodeBlock style={{ marginTop: '0.2rem' }}>{result.responseBody ? prettyJson(result.responseBody) : '(empty body)'}</CodeBlock>
                  <div style={{ position: 'absolute', top: '0.35rem', right: '0.35rem' }}>
                    <CopyButton text={prettyJson(result.responseBody ?? '')} small />
                  </div>
                </div>
              )}
            </div>
          )}
          {result.success && (
            <>
              <div style={{ marginTop: '0.5rem', fontSize: '0.72rem', color: 'var(--text-muted)', background: 'rgba(234,179,8,0.06)', border: '1px solid rgba(234,179,8,0.15)', borderRadius: 6, padding: '0.4rem 0.6rem' }}>
                💡 Webhook acknowledged. Now waiting for your server to call back with <code>POST /api/v1/agent-response</code>...
              </div>
              {showWaiter && (
                <SandboxResponseWaiter
                  agentId={agentId}
                  invocationId={result.invocationId}
                  apiKeyPrefix={apiKeyPrefix}
                />
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
